import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CHECKOUT_DATA } from '@/constants/queries';
import {
  APPLE_PAY_TAB_DATA_MESSAGE,
  APPLE_PAY_TAB_HANDOFF_TIMEOUT_MS,
  APPLE_PAY_TAB_READY_MESSAGE,
  type ApplePayTabDataMessage,
} from '@/utils/applePayTabFallback';
import { iframeMessenger, type PromoCodeData } from '@/services/iframeMessenger';
import type { AddressDetails } from '@/types/context';
import { logError, logInfo } from '@/utils/logger';

export type ApplePayTabBootstrapStatus =
  /** Not the fallback tab — hook is inert. */
  | 'idle'
  /** Handshake in flight; keep the boot skeleton, do NOT fetch `/checkout/v3`. */
  | 'waiting'
  /** Opener's payload seeded into the query cache — render without fetching. */
  | 'seeded'
  /** No opener / no reply in time — let the regular `/checkout/v3` fetch run. */
  | 'fallback';

export interface ApplePayTabBootstrapResult {
  status: ApplePayTabBootstrapStatus;
  /** Promo already applied in the opener (analytics only — pricing arrives merged into the payload). */
  promoCode: PromoCodeData | null;
  /** ZIP / address as edited in the opener, which the cached payload predates. */
  addressDetails: AddressDetails | null;
}

/**
 * Tab side of the iOS <= 16 Apple Pay new-tab fallback handshake.
 *
 * Posts `apple-pay-tab:ready` to `window.opener` (the framed checkout that
 * opened us — same origin) and waits for the `apple-pay-tab:data` reply
 * carrying the already-fetched `/checkout/v3` payload, which is seeded
 * straight into the React Query cache (`staleTime: Infinity` keeps it from
 * refetching). The tab therefore never re-issues the init call.
 *
 * Last resort only: when the opener is gone (original tab closed, this tab
 * reloaded) or silent past the timeout, resolve to `fallback` so the caller
 * enables the normal fetch instead of rendering a broken page.
 */
export const useApplePayTabBootstrap = (enabled: boolean): ApplePayTabBootstrapResult => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<ApplePayTabBootstrapStatus>(enabled ? 'waiting' : 'idle');
  const [promoCode, setPromoCode] = useState<PromoCodeData | null>(null);
  const [addressDetails, setAddressDetails] = useState<AddressDetails | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const opener = window.opener as Window | null;
    if (!opener) {
      logInfo('Apple Pay tab fallback: no opener — falling back to /checkout/v3 fetch');
      setStatus('fallback');
      return;
    }

    // Publisher SDK contract: lifecycle events (order success/failure,
    // payment intents, ...) normally postMessage to `window.parent`, but
    // this tab is top-level (`parent === self`), so they would vanish.
    // Retarget the messenger at the opener iframe's parent — the publisher
    // page hosting the checkout — so the SDK still hears about the payment
    // made in this tab. The opener is same-origin, so reading `.parent` is
    // allowed; posting to the cross-origin publisher window uses the
    // messenger's existing `*` target origin.
    try {
      iframeMessenger.setTargetWindow(opener.parent ?? opener);
    } catch (error) {
      logError('Apple Pay tab fallback: could not retarget publisher SDK events at the opener parent', error);
    }

    let settled = false;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      // Only the window that opened this tab may seed the checkout — ignore
      // any other same-origin sender racing the real opener.
      if (!event.source || event.source !== opener) return;
      const data = event.data as Partial<ApplePayTabDataMessage> | null;
      if (data?.type !== APPLE_PAY_TAB_DATA_MESSAGE) return;
      settled = true;
      window.clearTimeout(timeoutId);
      window.removeEventListener('message', handleMessage);
      if (data.checkoutData && typeof data.checkoutData === 'object') {
        queryClient.setQueryData([CHECKOUT_DATA], data.checkoutData);
        setPromoCode(data.promoCode ?? null);
        setAddressDetails(data.addressDetails ?? null);
        logInfo('Apple Pay tab fallback: checkout data seeded from opener');
        setStatus('seeded');
      } else {
        logError('Apple Pay tab fallback: opener replied without checkout data');
        setStatus('fallback');
      }
    };
    window.addEventListener('message', handleMessage);

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', handleMessage);
      logError('Apple Pay tab fallback: handshake timed out — falling back to /checkout/v3 fetch');
      setStatus('fallback');
    }, APPLE_PAY_TAB_HANDOFF_TIMEOUT_MS);

    try {
      opener.postMessage({ type: APPLE_PAY_TAB_READY_MESSAGE }, window.location.origin);
    } catch (error) {
      logError('Apple Pay tab fallback: failed to reach opener', error);
      settled = true;
      window.clearTimeout(timeoutId);
      window.removeEventListener('message', handleMessage);
      setStatus('fallback');
    }

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('message', handleMessage);
    };
  }, [enabled, queryClient]);

  return { status, promoCode, addressDetails };
};
